/**
 * Performance History hook (CR-4751).
 *
 * Fetches the data needed by the Cached Performance tab:
 *   - Latest snapshot per metric type (for KPI cards)
 *   - Trend series for every metric that drives a chart
 *   - Target thresholds from SupplierPerformanceSettings for target lines
 *
 * Accepts a period type + optional date range so the caller (tab) can surface
 * a "Last 3/6/12 months / Custom" selector without the hook knowing anything
 * about UI widgets.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  DAILY_HIDDEN_METRICS,
  dashboardSnapshotsAPI,
  type DashboardSnapshot,
  type MetricType,
  type PeriodType,
  type ScopeParams,
  type TrendPoint,
} from '../lib/api/dashboard-snapshots';
import { performanceSettingsAPI } from '../lib/api/performanceSettings';
import type { KPITargetKind, KPITargetRule } from '../lib/api/performanceSettings';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from './useFetchData';
import { usePortalMode } from './usePortalMode';

export interface PerformanceTargets {
  ppm: number | null;
  otif: number | null;
  otd: number | null;
  /** CR-6266: target ranges that replace a default between dates. */
  ranges?: Partial<Record<KPITargetKind, KPITargetRule[]>>;
}

const TARGET_KPIS: KPITargetKind[] = ['ppm', 'otif', 'otd'];

export interface PerformanceHistoryData {
  latest: DashboardSnapshot[];
  /**
   * State metrics (open_order_value, arrears_*, ncr_open_count,
   * active_supplier_count) pulled from the DAILY period and surfaced here so
   * the UI can fall back when the selected period has no state-metric
   * coverage (e.g. Weekly/Quarterly have none — they aren't backfilled).
   */
  stateLatest: DashboardSnapshot[];
  trends: Record<MetricType, TrendPoint[]>;
  targets: PerformanceTargets;
}

export interface PerformanceHistoryOptions {
  periodType?: PeriodType;
  dateFrom?: string;
  dateTo?: string;
  /** CR-4751: the dashboard switcher's site / company / group (getViewQueryParams). */
  scope?: ScopeParams;
  /** CR-4751: the viewer's display currency; money comes back converted into it. */
  currency?: string;
}

export interface PerformanceHistoryResult {
  data: PerformanceHistoryData | null;
  loading: boolean;
  error: string | null;
  /** CR-4751: the trends call failed — the charts are empty because of an error, not missing data. */
  trendsError: boolean;
  refetch: () => Promise<void>;
}

const CHART_METRICS: MetricType[] = [
  'open_order_value',
  'total_spend',
  'monthly_spend',
  'arrears_value',
  'arrears_count',
  'arrears_opening_value',
  'arrears_new_value',
  'arrears_resolved_value',
  'arrears_closing_value',
  'otif_percentage',
  'otd_percentage',
  'on_time_count',
  'early_count',
  'late_count',
  'total_grns',
  'ppm',
  'ncr_count',
  'ncr_open_count',
  'active_supplier_count',
  'appeals_count',
];

/**
 * Fetch the full data bundle for the Cached Performance tab.
 *
 * @param options - Period type + optional date range
 * @returns PerformanceHistoryResult with latest + trends + targets
 */
export function usePerformanceHistory(
  options: PeriodType | PerformanceHistoryOptions = 'monthly',
): PerformanceHistoryResult {
  const normalized: PerformanceHistoryOptions =
    typeof options === 'string' ? { periodType: options } : options;
  const periodType: PeriodType = normalized.periodType ?? 'monthly';
  const { dateFrom, dateTo, scope, currency } = normalized;
  // A primitive dependency, so a new-but-equal scope object does not refetch.
  const scopeKey = JSON.stringify(scope ?? {});

  const [data, setData] = useState<PerformanceHistoryData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [trendsError, setTrendsError] = useState<boolean>(false);
  // Customer review 11 Sep: the settings endpoint is IsHelixOrCustomerAdmin —
  // a Customer User got a logged 403 on every load and no targets anyway.
  const { user } = useAuth();
  const { isHelix } = usePortalMode();
  const canReadPerfSettings = isHelix || Boolean(user?.is_company_admin || user?.can_manage_users);
  // CR-4751: the target-rules endpoint is IsHelixOrCustomerGroupAdmin, narrower
  // than the settings one, so a site-level admin logged three 403s per load.
  // Mirrors backend is_customer_group_admin(), as AdminSettings does.
  const canReadTargetRules = isHelix || Boolean(user?.company_type === 'helix' || user?.is_group_admin);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch DAILY latest too — state metrics are only forward-captured
      // on the daily cadence (backfill skips them for historical accuracy).
      // Surfaced as a fallback so Weekly/Quarterly views can still show the
      // current Open Orders / Arrears / Open NCRs values.
      // CR-4751: every chart metric in ONE call (it was one call per metric,
      // 20 per load). Daily leaves out what a single day does not measure.
      const metrics = periodType === 'daily'
        ? CHART_METRICS.filter((m) => !DAILY_HIDDEN_METRICS.includes(m))
        : CHART_METRICS;
      const scopeParams = JSON.parse(scopeKey) as ScopeParams;
      const [latestRes, stateLatestRes, settingsRes, rangeResponses, trendsRes] = await Promise.all([
        dashboardSnapshotsAPI.latest(periodType, scopeParams, currency),
        periodType === 'daily'
          ? Promise.resolve({ data: [] as DashboardSnapshot[] })
          : dashboardSnapshotsAPI.latest('daily', scopeParams, currency)
            .catch(() => ({ data: [] as DashboardSnapshot[] })),
        canReadPerfSettings ? performanceSettingsAPI.getSettings().catch(() => null) : Promise.resolve(null),
        // CR-6266: the target ranges, for the target line to step where one
        // starts. The same audience as the settings; a refusal leaves none.
        canReadTargetRules
          ? Promise.all(TARGET_KPIS.map((kpi) => Promise.resolve()
            .then(() => performanceSettingsAPI.getKPITargetRules(kpi))
            .then((r) => [kpi, r.data.results] as const)
            .catch(() => [kpi, [] as KPITargetRule[]] as const)))
          : Promise.resolve([]),
        // A failed trends call leaves the charts empty, not the whole tab —
        // and says so (trendsError), rather than reading as "no snapshots yet".
        dashboardSnapshotsAPI
          .trendsMany({
            metric_types: metrics,
            period_type: periodType,
            scope: scopeParams,
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
            ...(currency ? { currency } : {}),
          })
          .then((r) => ({ data: r.data, failed: false }))
          .catch((err: unknown) => {
            console.error('Failed to load cached performance trends:', err);
            return { data: {} as Partial<Record<MetricType, TrendPoint[]>>, failed: true };
          }),
      ]);
      setTrendsError(trendsRes.failed);
      const trends = Object.fromEntries(
        CHART_METRICS.map((metric) => [metric, trendsRes.data[metric] ?? []]),
      ) as Record<MetricType, TrendPoint[]>;

      const settings = settingsRes?.data;
      const targets: PerformanceTargets = {
        // CR-6266: the PPM target, not the "good" colour band boundary it
        // used to borrow — the same mix-up CR-6267 fixed for OTIF below.
        ppm: settings ? Number(settings.ppm_target) : null,
        // CR-6267: read the dedicated target fields. This used to read
        // otif_good_threshold — a *colour* band boundary — as the chart
        // target line, which those fields never meant; the colour bands are
        // now per-grade rows and carry no single "target" value.
        otif: settings ? Number(settings.otif_target) : null,
        otd: settings ? Number(settings.otd_target) : null,
        ranges: Object.fromEntries(rangeResponses),
      };

      setData({
        latest: latestRes.data,
        stateLatest: stateLatestRes.data,
        trends,
        targets,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load performance history'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodType, dateFrom, dateTo, scopeKey, currency, canReadPerfSettings, canReadTargetRules]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, trendsError, refetch: fetchAll };
}
